import { useEffect, useMemo } from "react";
import DOMPurify from "dompurify";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { FAQ_GROUPS, FAQ_INTRO, ALL_FAQ_QUESTIONS } from "@/lib/faq";
import { SITE } from "@/lib/site";
import { ArrowUpRight, Phone, Mail } from "lucide-react";

function slugifyQ(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
}

export default function FAQ() {
  // Update document title for SEO when visiting /faq
  useEffect(() => {
    const prev = document.title;
    document.title =
      "Real Estate & Mortgage Q&A — J.J. Botelho | Emerald Coast, FL";
    return () => {
      document.title = prev;
    };
  }, []);

  // FAQPage JSON-LD schema — sanitize before injecting into <script>
  const faqSchemaJson = useMemo(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: ALL_FAQ_QUESTIONS.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.a,
        },
      })),
    };
    // JSON.stringify already escapes; DOMPurify adds defense-in-depth
    return DOMPurify.sanitize(JSON.stringify(schema));
  }, []);

  return (
    <main data-testid="faq-page" className="bg-bone min-h-screen">
      <Navbar />

      {/* JSON-LD FAQPage — picked up by Google + AI search engines */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: faqSchemaJson }}
      />

      {/* Hero */}
      <section className="relative pt-36 pb-16 md:pt-44 md:pb-20 bg-ink text-white overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-brass/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          <p className="overline text-brass" data-testid="faq-overline">
            {FAQ_INTRO.eyebrow}
          </p>
          <h1
            data-testid="faq-heading"
            className="mt-6 font-display text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight"
          >
            Real answers,
            <br />
            <span className="italic text-brass">no fluff.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-white/75 text-base md:text-lg leading-relaxed">
            {FAQ_INTRO.subtitle}
          </p>
        </div>
      </section>

      {/* Category quick-jump */}
      <section className="bg-sand border-b border-border2 sticky top-20 z-30 backdrop-blur-md bg-sand/85">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 md:py-4 flex flex-nowrap md:flex-wrap gap-2 overflow-x-auto md:overflow-visible no-scrollbar">
          {FAQ_GROUPS.map((g) => (
            <a
              key={g.id}
              href={`#${g.id}`}
              data-testid={`faq-jump-${g.id}`}
              className="flex-shrink-0 whitespace-nowrap text-[11px] tracking-widest uppercase text-text2 hover:text-ink border border-border2 hover:border-ink rounded-full px-3 py-1.5 md:px-4 md:py-2 transition-colors duration-200 bg-white/70"
            >
              {g.title}
            </a>
          ))}
        </div>
      </section>

      {/* FAQ groups */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 space-y-16 md:space-y-20">
          {FAQ_GROUPS.map((group) => (
            <div key={group.id} id={group.id} className="scroll-mt-40">
              <p className="overline text-ink">{group.title}</p>
              <h2 className="mt-4 font-display text-ink text-3xl sm:text-4xl tracking-tight leading-[1.1]">
                {group.title}
              </h2>

              <Accordion
                type="multiple"
                className="mt-8 divide-y divide-border2 border-y border-border2"
                data-testid={`faq-group-${group.id}`}
              >
                {group.questions.map((item, idx) => {
                  const qid = `${group.id}-${slugifyQ(item.q)}`;
                  return (
                  <AccordionItem
                    key={qid}
                    value={qid}
                    className="border-b-0"
                  >
                    <AccordionTrigger
                      data-testid={`faq-q-${group.id}-${idx}`}
                      className="font-display text-ink text-lg md:text-xl text-left tracking-tight py-6 hover:no-underline group"
                    >
                      <span className="pr-6">{item.q}</span>
                    </AccordionTrigger>
                    <AccordionContent
                      data-testid={`faq-a-${group.id}-${idx}`}
                      className="text-text2 text-[0.95rem] md:text-base leading-relaxed pb-7 pr-4"
                    >
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative py-20 md:py-28 bg-ink text-white overflow-hidden grain">
        <div className="absolute -bottom-40 -left-40 w-[480px] h-[480px] rounded-full bg-brass/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="overline text-brass inline-flex">Still curious?</p>
          <h2 className="mt-6 font-display text-white text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight">
            Ask me anything
            <br />
            <span className="italic text-brass">— the answer's free.</span>
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-white/75 text-base md:text-lg leading-relaxed">
            If your question isn't here, send it over. I'll answer it personally —
            usually within a few hours.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a
              href={SITE.phoneHref}
              data-testid="faq-cta-call"
              className="inline-flex items-center gap-2 bg-brass text-ink px-7 py-4 rounded-full text-sm font-semibold tracking-wide hover:bg-white transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call {SITE.phone}
            </a>
            <a
              href={`mailto:${SITE.emails.realty}`}
              data-testid="faq-cta-email"
              className="inline-flex items-center gap-2 border border-white/40 text-white px-7 py-4 rounded-full text-sm font-medium hover:bg-white hover:text-ink transition-colors"
            >
              <Mail className="w-4 h-4" />
              Email J.J.
            </a>
            <a
              href="/#contact"
              data-testid="faq-cta-form"
              className="group inline-flex items-center gap-2 text-white text-sm font-medium border-b border-white/40 pb-1 px-2 hover:text-brass hover:border-brass transition-colors"
            >
              Open contact form
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
